/**
 * Dates of every Sentinel-2 overpass over what is currently on screen.
 *
 * The second imagery layer of docs/MAP_IMAGERY_RESEARCH.md §2, and the one that costs
 * money, so the guards are the design and not an afterthought:
 *
 *   1. the layer is off by default and nothing is requested while it is off;
 *   2. below zoom 14 nothing is requested at all — the picture would be a smudge and the
 *      bill identical;
 *   3. the map has to have moved 30 km from the last fetched centre; panning inside an area
 *      already paid for is free;
 *   4. 2 s of quiet after the map stops, so dragging across a region is one request, not forty;
 *   5. a process-local cache in front of the network, mirroring the server's 6 h one
 *      (backend/app/api/satellite.py) — toggling the layer off and on again costs nothing.
 *
 * 2, 3 and 4 are ported from the main project's `useSatelliteTimeline`, which has run them
 * in production. The decision itself lives in lib/viewport.ts as a pure function so it can
 * be tested without a map, and 5 is new here.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "../api/client";
import { DATES_TTL_MS, TtlCache } from "../lib/ttlCache";
import {
  bboxParam,
  datesFetchDecision,
  DATES_DEBOUNCE_MS,
  type SkipReason,
  type Viewport,
} from "../lib/viewport";

const cache = new TtlCache<string[]>(DATES_TTL_MS);

/** Exported for tests: a stale cache is indistinguishable from a broken guard otherwise. */
export const resetViewportDatesCache = (): void => cache.clear();

export interface ViewportDates {
  dates: string[];
  loading: boolean;
  error: string | null;
  /** Which guard stopped the request, so the interface can say why the list is empty. */
  skipped: SkipReason;
  /** The bbox the dates belong to, in the form the tiles endpoint takes. */
  bbox: string | null;
}

export const useViewportDates = (viewport: Viewport | null, active: boolean): ViewportDates => {
  const [dates, setDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<SkipReason>(null);
  const [bbox, setBbox] = useState<string | null>(null);

  const lastCenter = useRef<[number, number] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchDates = useCallback(async (view: Viewport) => {
    const decision = datesFetchDecision(view, lastCenter.current);
    setSkipped(decision);
    // "near" means the answer on screen is still the right one; only "zoom" empties it,
    // because at that scale we genuinely do not know what is down there.
    if (decision === "zoom") {
      setDates([]);
      setBbox(null);
      return;
    }
    if (decision === "near") return;

    const param = bboxParam(view.bbox);
    const hit = cache.get(param);
    if (hit) {
      lastCenter.current = view.center;
      setDates(hit);
      setBbox(param);
      setError(null);
      return;
    }

    setLoading(true);
    try {
      const loaded = await api.listSatelliteDates(param);
      lastCenter.current = view.center;
      cache.put(param, loaded.dates);
      setDates(loaded.dates);
      setBbox(param);
      setError(null);
    } catch (cause) {
      setDates([]);
      setBbox(null);
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  const primed = useRef(false);

  useEffect(() => {
    if (!active || !viewport) {
      // Switching the layer off must also forget where we last looked, otherwise the 30 km
      // guard would suppress the first fetch after switching it back on somewhere else.
      if (!active) {
        lastCenter.current = null;
        primed.current = false;
      }
      return;
    }
    // Turning the layer on is an explicit ask, so it answers at once; only map movement is
    // debounced, which is what the 2 s is for.
    if (!primed.current) {
      primed.current = true;
      void fetchDates(viewport);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void fetchDates(viewport), DATES_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [active, viewport, fetchDates]);

  return { dates, loading, error, skipped, bbox };
};
